import { createContext, useContext, useEffect, useMemo, type ReactNode } from 'react';
import { formatters, translate, type Dicts, type Formatters, type Params } from './engine';
import { pickLocalized, type Lang } from './langs';

/** Chaves de plural (`x.one`, `x.other`) também podem ser chamadas pela base (`x`) com `count`. */
export type PluralBase<K extends string> = K extends `${infer B}.one` ? B : never;

export interface I18nValue<K extends string = string> {
  lang: Lang;
  /** Traduz uma chave do dicionário. */
  t: (key: K | PluralBase<K>, params?: Params) => string;
  /** A chave existe no dicionário? (para textos opcionais, como o "por quê" de um erro). */
  has: (key: string) => boolean;
  /** Texto localizado vindo da API (`{en, pt-BR, es}`). */
  l: (v: unknown) => string;
  fmt: Formatters;
}

const Ctx = createContext<I18nValue | null>(null);

export function I18nProvider({ lang, dicts, children }: { lang: Lang; dicts: Dicts; children: ReactNode }) {
  const value = useMemo<I18nValue>(
    () => ({
      lang,
      t: (key, params) => translate(dicts, lang, key, params),
      has: (key) => key in (dicts[lang] ?? {}) || key in dicts.en,
      l: (v) => pickLocalized(v, lang),
      fmt: formatters(lang),
    }),
    [lang, dicts],
  );
  // O atributo lang do documento acompanha a interface (leitores de tela).
  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/** Acesso tipado às traduções: cada aplicação passa o próprio tipo de chave. */
export function useI18n<K extends string = string>(): I18nValue<K> {
  const v = useContext(Ctx);
  if (!v) throw new Error('useI18n fora do I18nProvider');
  return v as unknown as I18nValue<K>;
}
